import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import MobileLayout from "@/components/MobileLayout";
import { supabase } from "@/integrations/supabase/client";
import vyvIcon from "@/assets/vyv-icon.png";
import { useLanguage } from "@/i18n/LanguageProvider";
import { newProfileLanguagePatch } from "@/lib/profileLanguage";
import { capitalizeName } from "@/lib/formatName";
import { analytics } from "@/lib/analytics";

const Processing = () => {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const [stepIndex, setStepIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [userName, setUserName] = useState("");
  // Run-once-per-mount guard for the profile save + its analytics. The progress
  // interval overshoots 100 (…→100.4, then clamps →100.0), so [progress] changes
  // twice while >= 100 and the save effect below re-fires ~40ms apart → the
  // same-second duplicate avatar_created / onboarding_completed seen in PostHog.
  // Mount-scoped ON PURPOSE (not a module-level flag): a LATER visit — editing
  // the avatar from Settings — mounts Processing fresh, gets a fresh ref, and
  // counts again, which is the legitimate re-fire we must preserve.
  const savedRef = useRef(false);
  const [avatarSubMsg, setAvatarSubMsg] = useState("");
  const [processingError, setProcessingError] = useState(false);

  const steps = [
    { text: t("processing.step1"), sub: t("processing.step1sub") },
    { text: t("processing.step2"), sub: t("processing.step2sub") },
    { text: t("processing.step3"), sub: t("processing.step3sub") },
    { text: t("processing.step4"), sub: avatarSubMsg },
    { text: t("processing.step5"), sub: "" },
  ];

  useEffect(() => {
    const onboarding = JSON.parse(localStorage.getItem("vyv_onboarding") || "{}");
    if (onboarding.name) setUserName(onboarding.name);
    if (onboarding.avatarType === "photo") setAvatarSubMsg(t("processing.avatarPhoto"));
    else if (onboarding.avatarType === "style") setAvatarSubMsg(t("processing.avatarStyle"));
    else setAvatarSubMsg(t("processing.avatarPlaceholder"));
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((p) => { if (p >= 100) { clearInterval(interval); return 100; } return p + 0.8; });
    }, 40);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setStepIndex(Math.min(Math.floor(progress / 20), steps.length - 1));
  }, [progress, steps.length]);

  useEffect(() => {
    if (progress >= 100) {
      if (savedRef.current) return;
      savedRef.current = true;
      const saveProfile = async (): Promise<boolean> => {
        const onboarding = JSON.parse(localStorage.getItem("vyv_onboarding") || "{}");
        // avatar_created `mode` follows the onboarding_started GATE (the edit
        // flag OnboardingAvatar threads from the SettingsAvatars nav-state) — NOT
        // the edit-vs-create branch below (which is driven by onboarding_complete
        // and wrongly tags the Mirror-CTA first-avatar path, onboarding_complete=
        // true but genuine onboarding, as an edit). Default 'onboarding' is safe.
        const avatarMode: "onboarding" | "edit" =
          onboarding.editEntry === true ? "edit" : "onboarding";
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
          localStorage.removeItem("vyv_onboarding");
          return true;
        }

        // Trilha A v2 (Iter 10): probe DB to detect edit-mode vs first-time
        // onboarding. Settings → "Novo avatar" routes through Processing
        // but the user already has a profile; the previous guard treated
        // this as broken onboarding and redirected to /identity.
        const { data: existing, error: probeErr } = await supabase
          .from("user_profiles")
          .select("onboarding_complete")
          .eq("user_id", user.id)
          .maybeSingle();

        if (probeErr) {
          console.error("[Processing] profile probe failed:", probeErr);
          setProcessingError(true);
          return false;
        }

        const isAvatarEditMode = existing?.onboarding_complete === true;

        if (isAvatarEditMode) {
          // Avatar swap from Settings — only patch avatar-related fields.
          // Never touch first_name / age / sex / onboarding_complete.
          const avatarPatch: Record<string, any> = {};
          if (onboarding.avatarType) avatarPatch.avatar_type = onboarding.avatarType;
          if (onboarding.avatarSource) avatarPatch.avatar_source = onboarding.avatarSource;
          if (onboarding.avatarUrl) {
            avatarPatch.avatar_url = onboarding.avatarUrl;
            avatarPatch.avatar_created_at = new Date().toISOString();
          }
          // Post-W4: OnboardingAvatar only persists avatarType in
          // {'metaperson','placeholder'}. The legacy 'avaturn' branch is
          // dead; explicit provider patch handled by the metaperson DB
          // trigger via user_avatars dual-write.

          if (Object.keys(avatarPatch).length === 0) {
            console.warn("[Processing] edit mode but no avatar fields in localStorage — skipping update");
            localStorage.removeItem("vyv_onboarding");
            return true;
          }

          const { error: updateErr } = await supabase
            .from("user_profiles")
            .update(avatarPatch)
            .eq("user_id", user.id);

          if (updateErr) {
            console.error("[Processing] avatar patch failed:", updateErr);
            setProcessingError(true);
            return false;
          }

          console.info("[Processing] avatar-edit patch applied:", avatarPatch);
          analytics.avatarCreated(onboarding.avatarType, avatarMode);
          localStorage.removeItem("vyv_onboarding");
          return true;
        }

        // First-time onboarding path. Apple Guideline 5.1.1(v): age + sex are
        // optional, so we NEVER bounce back for a missing sex. Empty fields
        // persist as NULL; sex is only written when a valid value was picked.
        const profileData: Record<string, any> = {
          user_id: user.id,
          first_name: capitalizeName(onboarding.name) || null,
          age: parseInt(onboarding.age) || null,
          onboarding_complete: true,
          avatar_source: onboarding.avatarSource || null,
          avatar_type: onboarding.avatarType || "placeholder",
        };

        if (["male", "female", "other"].includes(onboarding.sex)) {
          profileData.sex = onboarding.sex;
        }

        // Post-W4: provider+url cascade for catalog selections is owned by
        // the user_avatars INSERT trigger (sync_active_avatar_to_profile).
        // Onboarding flow no longer hand-writes provider here.

        // Carimba o idioma detectado SÓ se o perfil está nascendo agora — o
        // helper devolve {} para uma linha existente, então um re-run do
        // onboarding nunca apaga a escolha de idioma do usuário.
        const langPatch = await newProfileLanguagePatch(user.id, lang);

        const { data, error } = await supabase.from("user_profiles").upsert(
          { ...profileData, ...langPatch } as any,
          { onConflict: "user_id" }
        ).select();
        if (error) {
          console.error("[Processing] Onboarding save error:", error);
          setProcessingError(true);
          return false;
        }
        if (!Array.isArray(data) || data.length === 0) {
          console.error("[Processing] Onboarding save returned 0 rows — likely RLS block");
          setProcessingError(true);
          return false;
        }

        analytics.avatarCreated(onboarding.avatarType, avatarMode);
        analytics.onboardingCompleted();
        localStorage.removeItem("vyv_onboarding");
        return true;
      };
      saveProfile().then((ok) => {
        if (ok) {
          // Voice-first: end of first-use onboarding drops straight into the
          // Espelho (/mirror), avatar ready — not the /home dashboard.
          setTimeout(() => navigate("/mirror"), 1200);
        }
      }).catch((err) => {
        console.error("[Processing] saveProfile threw:", err);
        setProcessingError(true);
      });
    }
  }, [progress, navigate, lang]);

  if (processingError) {
    return (
      <MobileLayout noAnimation className="justify-center items-center relative overflow-hidden">
        <div className="flex flex-col items-center gap-6 px-8 text-center">
          <motion.img src={vyvIcon} alt="VYV" className="w-14 h-14 object-contain opacity-60" />
          <p className="font-display text-sm text-foreground">
            {t("processing.saveError")}
          </p>
          <button
            onClick={() => {
              setProcessingError(false);
              // PRESERVE: saveProfile is gated by useEffect([progress, navigate])
              // firing when progress hits 100. Clearing the error alone doesn't
              // re-trigger the save — onboarding error recovery depends on a
              // full re-mount. Soft refresh requires restructuring the progress
              // timer/save effects; deferred to W3 alongside Capacitor prep.
              window.location.reload();
            }}
            className="font-display text-[13px] font-semibold px-6 py-3 rounded-xl border border-primary/40 text-primary active:scale-[0.97] transition-all"
            style={{ boxShadow: "0 0 24px hsl(187 72% 50% / 0.20)" }}
          >
            {t("common.retry") || "Tentar novamente"}
          </button>
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout noAnimation className="justify-center items-center relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <motion.div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 rounded-full"
          style={{ background: "radial-gradient(circle, hsl(187 72% 50% / 0.08) 0%, transparent 70%)" }}
          animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.7, 0.3] }}
          transition={{ duration: 3, repeat: Infinity }} />
      </div>

      <div className="flex flex-col items-center gap-10">
        <motion.img src={vyvIcon} alt="VYV" className="w-14 h-14 object-contain"
          animate={{ opacity: [0.5, 0.8, 0.5] }} transition={{ duration: 3, repeat: Infinity }} />

        <div className="relative w-36 h-36">
          <svg className="w-36 h-36 -rotate-90" viewBox="0 0 144 144">
            <circle cx="72" cy="72" r="62" fill="none" stroke="hsl(220 22% 11%)" strokeWidth="3" />
            <motion.circle cx="72" cy="72" r="62" fill="none" stroke="hsl(187 72% 50%)" strokeWidth="3"
              strokeLinecap="round" strokeDasharray={`${2 * Math.PI * 62}`}
              strokeDashoffset={`${2 * Math.PI * 62 * (1 - progress / 100)}`}
              style={{ filter: "drop-shadow(0 0 6px hsl(187 72% 50% / 0.4))" }} />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="font-display text-3xl font-light text-foreground tabular-nums">{Math.round(progress)}</span>
          </div>
        </div>

        <div className="text-center min-h-[60px] flex flex-col items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.div key={stepIndex} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }} className="text-center">
              <p className="font-display text-sm font-medium text-foreground">{steps[stepIndex].text}</p>
              {steps[stepIndex].sub && <p className="text-muted-foreground text-xs mt-1">{steps[stepIndex].sub}</p>}
            </motion.div>
          </AnimatePresence>
        </div>

        <AnimatePresence>
          {progress >= 100 && userName && (
            <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
              className="font-display text-sm text-primary/70 text-center">
              {t("processing.welcome")} {userName}.
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </MobileLayout>
  );
};

export default Processing;
